import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import ImageUploader from './ImageUploader';
import RichTextEditor from './RichTextEditor';
import Toast from './Toast';
import { getSections, updateSection, createSection } from '../../services/api';
import { triggerDataRefresh } from '../../context/SiteDataContext';
import './admin.css';

const SectionEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    type: 'hero',
    order: 0,
    isActive: true,
    data: {
      title: '',
      subtitle: '',
      description: '',
      image: '',
      buyLink: '',
      buyBtn: 'Acheter',
      // For book type
      uptitle: 'Roman',
      images: [],
      themes: [],
    },
  });

  useEffect(() => {
    if (!isNew) {
      loadSection();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew]);

  const loadSection = async () => {
    try {
      const response = await getSections(true);
      const section = response.sections?.find(s => s.id === id);
      if (section) {
        setFormData({
          name: section.name,
          type: section.type,
          order: section.order,
          isActive: section.isActive,
          data: section.data || {},
        });
      } else {
        setToast({ message: 'Section non trouvée', type: 'error' });
      }
    } catch (err) {
      setToast({ message: err.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDataChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      data: {
        ...prev.data,
        [field]: value,
      },
    }));
  };

  const handleThemeChange = (index, field, value) => {
    const newThemes = [...(formData.data.themes || [])];
    newThemes[index] = { ...newThemes[index], [field]: value };
    handleDataChange('themes', newThemes);
  };

  const addTheme = () => {
    const newThemes = [...(formData.data.themes || []), { title: '', description: '', icon: 'assets/icons/touch.svg' }];
    handleDataChange('themes', newThemes);
  };

  const removeTheme = (index) => {
    const newThemes = formData.data.themes.filter((_, i) => i !== index);
    handleDataChange('themes', newThemes);
  };

  // Image management for book type
  const handleImageChange = (index, url) => {
    const newImages = [...(formData.data.images || ['', '', '', ''])];
    newImages[index] = url;
    handleDataChange('images', newImages);
  };

  const addImage = () => {
    const currentImages = formData.data.images || [];
    if (currentImages.length < 4) {
      handleDataChange('images', [...currentImages, '']);
    }
  };

  const removeImage = (index) => {
    const newImages = (formData.data.images || []).filter((_, i) => i !== index);
    handleDataChange('images', newImages);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setToast(null);
    setSaving(true);

    try {
      if (isNew) {
        await createSection('section', formData);
        setToast({ message: 'Section créée avec succès !', type: 'success' });
        setTimeout(() => navigate('/admin/sections'), 1500);
      } else {
        const result = await updateSection(id, 'section', formData);
        console.log('Update result:', result); // Debug
        setToast({ message: '✓ Modifications enregistrées !', type: 'success' });
        // Refresh site data
        triggerDataRefresh();
      }
    } catch (err) {
      console.error('Save error:', err); // Debug
      setToast({ message: err.message || 'Erreur lors de l\'enregistrement', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-loading">
          <div className="spinner"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-page">
        <header className="admin-page-header">
          <div>
            <h1>{isNew ? 'Nouvelle section' : `Modifier: ${formData.name}`}</h1>
            <p>{isNew ? 'Créer une nouvelle section de livre' : 'Modifier les informations de la section'}</p>
          </div>
        </header>

        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}

        <form onSubmit={handleSubmit} className="admin-form">
          <div className="admin-form-grid">
            <div className="admin-form-group">
              <label>Nom de la section</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Ex: Les ambitieuses"
                required
              />
            </div>

            <div className="admin-form-group">
              <label>Type</label>
              <select
                value={formData.type}
                onChange={(e) => handleChange('type', e.target.value)}
              >
                <option value="hero">Cover (Hero)</option>
                <option value="book">Livre (Détails)</option>
              </select>
            </div>

            <div className="admin-form-group">
              <label>Ordre d'affichage</label>
              <input
                type="number"
                value={formData.order}
                onChange={(e) => handleChange('order', parseInt(e.target.value))}
                min="0"
              />
            </div>

            <div className="admin-form-group">
              <label className="admin-checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => handleChange('isActive', e.target.checked)}
                />
                Section active
              </label>
            </div>
          </div>

          <hr className="admin-divider" />

          {formData.type === 'hero' && (
            <>
              <h3>Contenu Hero (Cover)</h3>
              <div className="admin-form-grid">
                <div className="admin-form-group">
                  <label>Sous-titre</label>
                  <input
                    type="text"
                    value={formData.data.subtitle || ''}
                    onChange={(e) => handleDataChange('subtitle', e.target.value)}
                    placeholder="Roman"
                  />
                </div>

                <div className="admin-form-group">
                  <label>Titre</label>
                  <input
                    type="text"
                    value={formData.data.title || ''}
                    onChange={(e) => handleDataChange('title', e.target.value)}
                    placeholder="Les ambitieuses"
                  />
                </div>

                <div className="admin-form-group full-width">
                  <label>Description</label>
                  <RichTextEditor
                    value={formData.data.description || ''}
                    onChange={(value) => handleDataChange('description', value)}
                    placeholder="Description du livre..."
                  />
                </div>

                <div className="admin-form-group">
                  <label>Lien d'achat</label>
                  <input
                    type="url"
                    value={formData.data.buyLink || ''}
                    onChange={(e) => handleDataChange('buyLink', e.target.value)}
                    placeholder="https://amazon.fr/..."
                  />
                </div>

                <div className="admin-form-group">
                  <label>Texte du bouton</label>
                  <input
                    type="text"
                    value={formData.data.buyBtn || ''}
                    onChange={(e) => handleDataChange('buyBtn', e.target.value)}
                    placeholder="Acheter"
                  />
                </div>

                <div className="admin-form-group full-width">
                  <label>Image de couverture</label>
                  <ImageUploader
                    currentImage={formData.data.image}
                    onUpload={(url) => handleDataChange('image', url)}
                  />
                </div>
              </div>
            </>
          )}

          {formData.type === 'book' && (
            <>
              <h3>Contenu Livre (Détails)</h3>
              <div className="admin-form-grid">
                <div className="admin-form-group">
                  <label>Sur-titre</label>
                  <input
                    type="text"
                    value={formData.data.uptitle || ''}
                    onChange={(e) => handleDataChange('uptitle', e.target.value)}
                    placeholder="Roman"
                  />
                </div>

                <div className="admin-form-group">
                  <label>Sous-titre</label>
                  <input
                    type="text"
                    value={formData.data.subtitle || ''}
                    onChange={(e) => handleDataChange('subtitle', e.target.value)}
                    placeholder="Les ambitieuses"
                  />
                </div>
              </div>

              <hr className="admin-divider" />
              
              <h4>Images du livre (1 à 4 images)</h4>
              <p className="admin-help-text">
                Ajoutez jusqu'à 4 images. L'affichage s'adapte automatiquement : 1 image en pleine largeur, 
                2 images côte à côte, 3 images en disposition asymétrique, 4 images en grille 2x2.
              </p>
              
              <div className="admin-images-grid">
                {(formData.data.images || []).map((image, index) => (
                  <div key={index} className="admin-image-item">
                    <div className="admin-image-header">
                      <span>Image {index + 1}</span>
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="admin-btn admin-btn-sm admin-btn-danger"
                      >
                        ✕
                      </button>
                    </div>
                    <ImageUploader
                      currentImage={image}
                      onUpload={(url) => handleImageChange(index, url)}
                    />
                  </div>
                ))}
              </div>
              
              {(formData.data.images || []).length < 4 && (
                <button
                  type="button"
                  onClick={addImage}
                  className="admin-btn admin-btn-secondary"
                  style={{ marginTop: '16px' }}
                >
                  + Ajouter une image ({(formData.data.images || []).length}/4)
                </button>
              )}

              <hr className="admin-divider" />

              <h4>Thèmes abordés</h4>
              {(formData.data.themes || []).map((theme, index) => (
                <div key={index} className="admin-theme-item">
                  <div className="admin-form-grid">
                    <div className="admin-form-group">
                      <label>Titre du thème</label>
                      <input
                        type="text"
                        value={theme.title || ''}
                        onChange={(e) => handleThemeChange(index, 'title', e.target.value)}
                        placeholder="L'amour et l'amitié"
                      />
                    </div>
                    <div className="admin-form-group full-width">
                      <label>Description</label>
                      <RichTextEditor
                        value={theme.description || ''}
                        onChange={(value) => handleThemeChange(index, 'description', value)}
                        placeholder="Description du thème..."
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeTheme(index)}
                    className="admin-btn admin-btn-sm admin-btn-danger"
                  >
                    Supprimer
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addTheme}
                className="admin-btn admin-btn-secondary"
              >
                + Ajouter un thème
              </button>
            </>
          )}

          <div className="admin-form-actions">
            <button
              type="button"
              onClick={() => navigate('/admin/sections')}
              className="admin-btn"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="admin-btn admin-btn-primary"
              disabled={saving}
            >
              {saving ? 'Enregistrement...' : (isNew ? 'Créer' : 'Enregistrer')}
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default SectionEditor;

